import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { catchError, finalize, map, Observable, of, switchMap, tap, throwError } from 'rxjs';
import { API_BASE_URL } from '../api/api.config';
import { AuthUser, LoginCredentials } from './auth.models';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http: HttpClient;
  private sessionChecked = false;

  readonly user = signal<AuthUser | null>(null);

  constructor(http: HttpClient) {
    this.http = http;
  }

  loadSession(force = false): Observable<AuthUser | null> {
    if (this.sessionChecked && !force) {
      return of(this.user());
    }

    return this.http.get<{ success: true; user: AuthUser }>(`${API_BASE_URL}/profile`).pipe(
      map(({ user }) => user),
      tap((user) => {
        localStorage.setItem('izia_username', user.username);
        this.user.set(user);
        this.sessionChecked = true;
      }),
      catchError(() => {
        this.user.set(null);
        this.sessionChecked = true;
        return of(null);
      }),
    );
  }

  login(credentials: LoginCredentials): Observable<AuthUser> {
    const body = new HttpParams()
      .set('username', credentials.username)
      .set('password', credentials.password)
      .set('login', 'Connexion');

    return this.http.post(`${API_BASE_URL}/login`, body, { responseType: 'text' }).pipe(
      tap(() => localStorage.setItem('izia_username', credentials.username)),
      switchMap(() => this.loadSession(true)),
      switchMap((user) => user ? of(user) : throwError(() => new HttpErrorResponse({
        status: 401,
        error: { success: false, error: { code: 'INVALID_CREDENTIALS', message: 'Identifiant ou mot de passe incorrect.' } },
      }))),
      tap((user) => {
        this.user.set(user);
        this.sessionChecked = true;
      }),
    );
  }

  forgotPassword(email: string): Observable<void> {
    const body = new HttpParams().set('email', email);
    return this.http.post(`${API_BASE_URL}/forgot-password`, body, { responseType: 'text' }).pipe(map(() => undefined));
  }

  resetPassword(token: string, password: string): Observable<void> {
    const body = new HttpParams().set('password', password).set('password_check', password);
    return this.http.post(`${API_BASE_URL}/reset-password/${encodeURIComponent(token)}`, body, { responseType: 'text' }).pipe(map(() => undefined));
  }

  logout(): Observable<void> {
    return this.http.get(`${API_BASE_URL}/logout`, { responseType: 'text' }).pipe(
      map(() => undefined),
      finalize(() => {
        localStorage.removeItem('izia_username');
        this.user.set(null);
        this.sessionChecked = true;
      }),
    );
  }

  private sessionUser(): AuthUser {
    return {
      id: null,
      username: localStorage.getItem('izia_username') || 'Utilisateur',
      email: null,
      first_name: '',
      last_name: '',
      role: null,
      tool_ids: [],
    };
  }
}
